/**
 * "Invite to Teslyn" from the Staff page and the invitations behind each row's note (decision 660;
 * docs/specs/cloud/staff-register.md §3.2). The invitation goes through the invite-user Edge Function with the
 * person's id; the database links the person to the sign-in on acceptance
 * (supabase/migrations/20261026001100_invite_links_staff.sql). RLS decides who reads `staff_invite`: tenant admins,
 * managers and office staff.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { InviteStaffRole, Person, StaffInvite } from '@teslyn/domain';
import { invite, type InviteResult } from '../users/api.ts';
import { saveStaff } from './api.ts';

const INVITE_COLUMNS =
  'id, tenant_id, user_id, email, person_id, source, invited_at, accepted_at, outcome, linked_person_id, candidates, note';

/**
 * The organisation's staff invitations, newest first. Empty where the database update is not applied yet (the
 * table does not exist) or the viewer may not read them.
 */
export async function loadStaffInvites(sb: SupabaseClient, tenantId: string): Promise<StaffInvite[]> {
  const r = await sb
    .from('staff_invite')
    .select(INVITE_COLUMNS)
    .eq('tenant_id', tenantId)
    .order('invited_at', { ascending: false });
  if (r.error) return [];
  return ((r.data ?? []) as StaffInvite[]).map((i) => ({ ...i, candidates: i.candidates ?? [] }));
}

export interface InviteToTeslynRequest {
  readonly tenantId: string;
  readonly person: Person;
  /** The address to invite: the register's, or the one typed when the person had none (saved to the register). */
  readonly email: string;
  readonly roles: readonly InviteStaffRole[];
  readonly redirectTo: string;
}

/**
 * Send the invitation for one register person. A person with no email gets the typed one in the register first, so
 * the register keeps it whatever happens to the invitation. Returns the person as saved and invite-user's answer.
 */
export async function inviteToTeslyn(
  sb: SupabaseClient,
  req: InviteToTeslynRequest,
): Promise<{ readonly person: Person; readonly result: InviteResult }> {
  const email = req.email.trim().toLowerCase();
  let person = req.person;
  if (!person.email?.trim()) {
    person = await saveStaff(sb, { ...person, email }, false);
  }
  const result = await invite(
    sb,
    { tenant_id: req.tenantId, email, kind: 'tenant', roles: req.roles, person_id: person.id },
    req.redirectTo,
  );
  return { person, result };
}

/**
 * After sign-in: settle the signed-in user's own pending invitations (my_staff_invites_accept), in case the sign-in
 * trigger did not run. Best effort and silent: before the database update the function does not exist.
 */
export async function settleMyStaffInvites(sb: SupabaseClient): Promise<void> {
  try {
    await sb.rpc('my_staff_invites_accept');
  } catch {
    /* nothing to settle, or not available yet */
  }
}
